// مشترک نسخه الکترونیک ملی (AP-MED-02؛ US-MED-031 تا 034، 092 تا 094): کلیدهای Query، برچسب وضعیت و علت، و پنل ارسال ملی
// که هم در تب ارسال COMP-RX-EDITOR و هم در SCR-STAFF-700 به کار می‌رود. متن‌ها فقط از فضای نام med.
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type MedNationalFailureReason,
  type MedNationalSubmissionStatus,
  type MedNationalSubmissionView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, StatusChip, toJalaliDateTime, useDisplaySettings, type DataTableColumn, type StatusTone } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API } from './common';

/** صف خطای ارسال ملی (US-MED-034). */
export const SCR_STAFF_701 = 'SCR-STAFF-701';

export const nationalKeys = {
  queue: (tenantId: string | undefined, status: string) => ['medorders', tenantId, 'national-queue', status] as const,
  byEncounter: (tenantId: string | undefined, encounterId: string) =>
    ['medorders', tenantId, 'national-encounter', encounterId] as const,
  byPatient: (tenantId: string | undefined, patientId: string) =>
    ['medorders', tenantId, 'national-patient', patientId] as const,
  catalog: (tenantId: string | undefined, insurer: string) => ['medorders', tenantId, 'insurer-catalog', insurer] as const,
  catalogItems: (tenantId: string | undefined, insurer: string, dataset: string, serviceType: string | null) =>
    ['medorders', tenantId, 'insurer-catalog-items', insurer, dataset, serviceType] as const,
  codeMaps: (tenantId: string | undefined, insurer: string) => ['medorders', tenantId, 'insurer-code-maps', insurer] as const,
  unmapped: (tenantId: string | undefined, insurer: string) => ['medorders', tenantId, 'insurer-unmapped', insurer] as const,
};

/** بیمه‌گر دارای اتصال رسمی در این برش (MED-Q3: تامین ← سلامت ← نیروهای مسلح). */
export const DEFAULT_INSURER = 'TAMIN';

const STATUS_TONE: Record<MedNationalSubmissionStatus, StatusTone> = {
  Pending: 'pending',
  Registered: 'done',
  Failed: 'error',
  AwaitingProviderAction: 'in_service',
  Deleted: 'cancelled',
};

/** برچسب وضعیت ارسال ملی. */
export function NationalStatusChip({ status }: { status: MedNationalSubmissionStatus }) {
  return <StatusChip tone={STATUS_TONE[status] ?? 'neutral'} labelKey={`med:national_status.${status}`} />;
}

/** متن علت شکست یا انتظار (AC-MED-034.1). */
export function reasonLabelKey(reason: MedNationalFailureReason): string {
  return `med:national_reason.${reason}`;
}

/**
 * پنل ارسال ملی یک مواجهه: دکمه «ثبت الکترونیک»، کد رهگیری، اقلام بی‌نگاشت و اقدام‌های ویرایش، حذف و استعلام وضعیت.
 * ارسال **ناهمگام** است، پس دکمه فقط ردیف صف می‌سازد و وضعیت با تازه‌سازی نما می‌آید (NFR PRD-MED §۱۲).
 */
export function NationalSubmissionPanel({
  encounterId,
  patientId,
  prescriptionId,
  canSubmit,
}: {
  /** نمای مواجهه (تب ارسال COMP-RX-EDITOR)؛ با `patientId` جایگزین می‌شود. */
  encounterId?: string;
  /** نمای مراجعه‌کننده (SCR-STAFF-700: «نسخه‌ها و وضعیت ملی»). */
  patientId?: string;
  prescriptionId?: string;
  canSubmit: boolean;
}) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const tenantId = session?.tenantId;

  const rows = useQuery({
    queryKey: encounterId
      ? nationalKeys.byEncounter(tenantId, encounterId)
      : nationalKeys.byPatient(tenantId, patientId ?? ''),
    enabled: Boolean(tenantId) && Boolean(encounterId ?? patientId),
    queryFn: async () => {
      if (encounterId) {
        return (
          await unwrap(
            tenantApi.GET('/api/v1/medical-orders/national-submissions/by-encounter/{encounterId}', {
              params: { path: { encounterId } },
            }),
          )
        ).items;
      }
      return (
        await unwrap(
          tenantApi.GET('/api/v1/medical-orders/national-submissions', { params: { query: { patientId } } }),
        )
      ).items;
    },
  });

  const submit = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/national-submissions', {
          body: { prescriptionId: prescriptionId ?? '', insurer: DEFAULT_INSURER, referentialServices: false },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => invalidate(error),
  });

  const invalidate = (error?: unknown) => {
    idempotency.settle(error);
    void queryClient.invalidateQueries({ queryKey: ['medorders', tenantId] });
  };

  // AC-MED-087.2: دریافت دستی سند رسمی بیمه‌گر پس از پایان سه تلاش ۲۴ ساعت (PHX-MED-0330).
  const fetchDocument = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/national-submissions/{submissionId}:fetch-document', {
          params: { path: { submissionId: id } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => invalidate(error),
  });

  const fetchStatus = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/national-submissions/{id}:fetch', {
          params: { path: { id } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => invalidate(error),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/national-submissions/{id}:delete', {
          params: { path: { id } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => invalidate(error),
  });

  const columns: DataTableColumn<MedNationalSubmissionView>[] = [
    { id: 'type', headerKey: 'med:national.presc_type', cell: (row) => <span>{faDigits(row.prescriptionTypeId)}</span> },
    { id: 'status', headerKey: 'med:national.status', cell: (row) => <NationalStatusChip status={row.status} /> },
    {
      id: 'tracking',
      headerKey: 'med:national.tracking_code',
      cell: (row) => <span data-testid={`med-national-tracking-${row.id}`}>{row.trackingCode ?? '—'}</span>,
    },
    { id: 'attempts', headerKey: 'med:national.attempts', cell: (row) => <span>{faDigits(row.attempts)}</span> },
    {
      id: 'reason',
      headerKey: 'med:national.error',
      cell: (row) => <span>{row.errorCode ? `${row.errorCode} · ${t(reasonLabelKey(row.failureReason))}` : '—'}</span>,
    },
    {
      id: 'created',
      headerKey: 'med:national.created_at',
      cell: (row) => <span>{toJalaliDateTime(row.createdAt, { timeZone })}</span>,
    },
    {
      id: 'actions',
      headerKey: 'med:national.items',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => fetchStatus.mutate(row.id)} data-testid={`med-national-fetch-${row.id}`}>
            {t('med:national.fetch')}
          </Button>
          {canSubmit && row.canDelete ? (
            <Button size="small" color="error" onClick={() => remove.mutate(row.id)} data-testid={`med-national-delete-${row.id}`}>
              {t('med:national.delete')}
            </Button>
          ) : null}
          {/* AC-MED-087.3: سند رسمی بیمه‌گر کنار نسخه؛ جایگزین نسخه امضاشده ققنوس نیست. */}
          {row.insurerDocumentId ? (
            <Button
              size="small"
              variant="text"
              component="a"
              href={`/api/v1/documents/documents/${row.insurerDocumentId}/content`}
              target="_blank"
              rel="noopener"
              data-testid={`med-national-insurer-document-${row.id}`}
            >
              {t('med:national.insurer_document')}
            </Button>
          ) : canSubmit && row.status === 'Registered' ? (
            <Button
              size="small"
              variant="text"
              onClick={() => fetchDocument.mutate(row.id)}
              disabled={fetchDocument.isPending}
              data-testid={`med-national-fetch-document-${row.id}`}
            >
              {t('med:national.fetch_insurer_document')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];

  const items = rows.data ?? [];
  const result = submit.data;

  return (
    <Stack spacing={2} data-testid="med-national-panel">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1">{t('med:national.title')}</Typography>
        {canSubmit && prescriptionId ? (
          <Button
            variant="contained"
            size="small"
            disabled={submit.isPending}
            onClick={() => submit.mutate()}
            data-testid="med-national-submit"
          >
            {submit.isPending ? t('med:national.submitting') : t('med:national.submit')}
          </Button>
        ) : null}
      </Stack>

      {result ? (
        <Alert severity="info" data-testid="med-national-queued">
          {t('med:national.queued')}
        </Alert>
      ) : null}

      {result && result.unmappedItemCodes.length > 0 ? (
        <Alert severity="warning" data-testid="med-national-unmapped">
          <AlertTitle>{t('med:national.unmapped_title')}</AlertTitle>
          {t('med:national.unmapped_hint')} {result.unmappedItemCodes.join(t('common:list_separator'))}
        </Alert>
      ) : null}

      {result && result.warnings.length > 0 ? (
        <Alert severity="warning" data-testid="med-national-warnings">
          <AlertTitle>{t('med:national.warnings_title')}</AlertTitle>
          {result.warnings.join(t('common:list_separator'))}
        </Alert>
      ) : null}

      <DataTable
        rows={items}
        columns={columns}
        getRowId={(row) => row.id}
        isLoading={rows.isLoading}
        error={rows.error}
        onRetry={() => void rows.refetch()}
        emptyTitleKey="med:national.empty"
        captionKey="med:national.title"
      />
    </Stack>
  );
}

/** صف خطای ارسال ملی (SCR-STAFF-701؛ US-MED-034): فیلتر وضعیت و ارسال دوباره. */
export function useNationalQueue(status: string) {
  const { tenantApi, session } = useTenantSession();
  const tenantId = session?.tenantId;
  const access = useAccess();

  return useQuery({
    queryKey: nationalKeys.queue(tenantId, status),
    enabled: Boolean(tenantId) && access.can('med.national_submission.manage'),
    queryFn: async () => {
      const query: { status?: MedNationalSubmissionStatus } =
        status === 'all' ? {} : { status: status as MedNationalSubmissionStatus };
      const response = await unwrap(
        tenantApi.GET('/api/v1/medical-orders/national-submissions', { params: { query } }),
      );
      return response.items;
    },
  });
}

export { API };
